// src/hooks/useGame.ts
import { useState, useEffect, useCallback } from 'react'
import { api } from '../api/client'
import type { Hunt, Egg, Progress, ChatMessage } from '../types'

interface GameState {
  hunt: Hunt | null
  eggs: Egg[]
  currentEggIndex: number
  progress: Progress[]
  loading: boolean
  error: string | null
  chatHistory: ChatMessage[]
  hintsUsed: number
  initialized: boolean
  eggStartTime: number
  averageFindTime: number
  difficulty: 'easy' | 'medium' | 'hard'
}

export function useGame(huntId: string, playerId: string) {
  const [state, setState] = useState<GameState>({
    hunt: null,
    eggs: [],
    currentEggIndex: 0,
    progress: [],
    loading: true,
    error: null,
    chatHistory: [],
    hintsUsed: 0,
    initialized: false,
    eggStartTime: Date.now(),
    averageFindTime: 0,
    difficulty: 'medium',
  })

  useEffect(() => {
    loadGame()
  }, [huntId, playerId])

  useEffect(() => {
    if (state.initialized) {
      setState((s) => ({ ...s, eggStartTime: Date.now() }))
    }
  }, [state.currentEggIndex, state.initialized])

  const loadGame = async () => {
    try {
      const [huntData, progressData] = await Promise.all([
        api.hunts.get(huntId),
        api.hunts.getProgress(huntId, playerId),
      ])

      const foundEggIds = new Set(progressData.progress.map((p) => p.egg_id))
      const nextIndex = huntData.eggs.findIndex((e) => !foundEggIds.has(e.id))

      setState({
        hunt: huntData.hunt,
        eggs: huntData.eggs,
        currentEggIndex: nextIndex >= 0 ? nextIndex : huntData.eggs.length,
        progress: progressData.progress,
        loading: false,
        error: null,
        chatHistory: [],
        hintsUsed: 0,
        initialized: true,
        eggStartTime: Date.now(),
        averageFindTime: 0,
        difficulty: 'medium',
      })
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: 'Kunde inte ladda jakten' }))
    }
  }

  const currentEgg = state.eggs[state.currentEggIndex] || null
  const isComplete =
    state.initialized &&
    state.eggs.length > 0 &&
    state.currentEggIndex >= state.eggs.length
  const totalEggs = state.eggs.length
  const foundCount = state.progress.length

  const recordFound = useCallback(
    async (photoKey?: string, foundLocation?: string) => {
      if (!currentEgg) return

      await api.hunts.recordFound(huntId, {
        player_id: playerId,
        egg_id: currentEgg.id,
        hints_used: state.hintsUsed,
        photo_key: photoKey,
        found_location: foundLocation || '',
        chat_log: state.chatHistory.map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
        })),
      })

      setState((s) => {
        const timeTaken = (Date.now() - s.eggStartTime) / 1000
        const prevFoundCount = s.progress.length
        const newAvg =
          prevFoundCount === 0
            ? timeTaken
            : (s.averageFindTime * prevFoundCount + timeTaken) /
              (prevFoundCount + 1)
        const newDifficulty: 'easy' | 'medium' | 'hard' =
          newAvg < 60 ? 'easy' : newAvg > 180 ? 'hard' : 'medium'

        return {
          ...s,
          progress: [
            ...s.progress,
            {
              id: '',
              player_id: playerId,
              egg_id: currentEgg.id,
              found_at: Date.now() / 1000,
              hints_used: s.hintsUsed,
              photo_key: photoKey || '',
              found_location: foundLocation || '',
              chat_log: s.chatHistory,
            },
          ],
          currentEggIndex: s.currentEggIndex + 1,
          chatHistory: [],
          hintsUsed: 0,
          eggStartTime: Date.now(),
          averageFindTime: newAvg,
          difficulty: newDifficulty,
        }
      })
    },
    [currentEgg, huntId, playerId, state.hintsUsed, state.chatHistory]
  )

  const addChatMessage = useCallback((msg: ChatMessage) => {
    setState((s) => ({
      ...s,
      chatHistory: [...s.chatHistory, msg],
    }))
  }, [])

  const useHint = useCallback(() => {
    setState((s) => ({ ...s, hintsUsed: Math.min(s.hintsUsed + 1, 3) }))
  }, [])

  return {
    ...state,
    currentEgg,
    isComplete,
    totalEggs,
    foundCount,
    difficulty: state.difficulty,
    averageFindTime: state.averageFindTime,
    eggStartTime: state.eggStartTime,
    recordFound,
    addChatMessage,
    useHint,
  }
}
